import { useCallback, useContext, useEffect, useState } from 'react';
import { useMutation } from '@apollo/client/react';
import clsx from 'clsx';
import debounce from 'lodash.debounce';

import TaskContext from '@atom/components/workOrderPreview/taskView/TaskContext';
import WorkOrderPreviewContext from '@atom/components/workOrderPreview/WorkOrderPreviewContext';
import { TASK_UPDATE } from '@atom/graph/task';
import { DatePicker, DateTimePicker } from '@atom/mui';
import {
  doesNotHaveRolePermissions,
  ROLE_SETS,
} from '@atom/utilities/authUtilities';
import { convertDateToMillisGMTMidday } from '@atom/utilities/timeUtilities';
import { showWorkDateTime } from '@atom/utilities/workPreferenceUtilities';

import classes from './taskView.module.css';

const DEBOUNCE_TIME = 1000;

const TaskCompletedTime = () => {
  const { workOrderDetail, refetch } = useContext(WorkOrderPreviewContext);
  const { task } = useContext(TaskContext);
  const [completionDate, setCompletionDate] = useState<Date>(
    task.completionDate ? new Date(task.completionDate) : null,
  );

  const [taskUpdate] = useMutation(TASK_UPDATE);

  useEffect(() => {
    setCompletionDate(
      task.completionDate && task.isCompleted
        ? new Date(task.completionDate)
        : null,
    );
  }, [task.completionDate, task.isCompleted]);

  const handleSubmit = async (date: Date) => {
    await taskUpdate({
      variables: {
        input: {
          id: task.id,
          workOrderId: workOrderDetail.id,
          completionDate: showWorkDateTime(workOrderDetail)
            ? date.valueOf()
            : convertDateToMillisGMTMidday(date),
        },
      },
    });

    refetch();
  };

  const debouncedHandleSubmit = useCallback(
    debounce(handleSubmit, DEBOUNCE_TIME),
    [task.id, workOrderDetail.id],
  );

  const onChange = (date?: Date) => {
    setCompletionDate(date);

    if (!date) {
      return;
    } else if (!Number.isNaN(date.valueOf())) {
      debouncedHandleSubmit(date);
    }
  };

  const onBlur = () => {
    if (!completionDate) {
      setCompletionDate(
        task.completionDate && task.isCompleted
          ? new Date(task.completionDate)
          : null,
      );
    }
  };

  const isDisabled =
    !task.isCompleted ||
    workOrderDetail.isClosed ||
    doesNotHaveRolePermissions(ROLE_SETS.MANAGER);

  return (
    <div
      className={clsx(
        classes.timeRowItem,
        showWorkDateTime(workOrderDetail) && classes.dateTimeInput,
      )}
    >
      <div className={classes.label}>Completed Date</div>
      <div className={classes.timePickerContainer}>
        {showWorkDateTime(workOrderDetail) ? (
          <DateTimePicker
            value={completionDate}
            onChange={onChange}
            disabled={isDisabled}
            onBlur={onBlur}
            data-cy="taskPreviewCompletedDateInput"
          />
        ) : (
          <DatePicker
            value={completionDate}
            onChange={onChange}
            disabled={isDisabled}
            onBlur={onBlur}
            data-cy="taskPreviewCompletedDateInput"
          />
        )}
      </div>
    </div>
  );
};

export default TaskCompletedTime;
